import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import { useIsMobile } from "../../hooks/use-mobile";
import { cn } from "../../utils";
import { Button } from "../button";
import { Input } from "../input";
import { Separator } from "../separator/Separator";
import { Sheet, SheetContent } from "../sheet";
import { Skeleton } from "../skeleton/Skeleton";
import { TooltipContent, TooltipCore, TooltipProvider, TooltipTrigger } from "../tooltip/Tooltip";
import { IconLayoutSidebar } from "@tabler/icons-react";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContextType = {
  state: "expanded" | "collapsed";
  open: boolean;
  baseOpen: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  expandOnHover: boolean;
  isHovered: boolean;
  setIsHovered: (hovered: boolean) => void;
  overlayOnHover: boolean;
  isManualCloseRef: React.RefObject<boolean>;
};

const SidebarContext = React.createContext<SidebarContextType | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (_open: boolean) => void;
    expandOnHover?: boolean;
    overlayOnHover?: boolean;
  }
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      expandOnHover = false,
      overlayOnHover = false,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const isManualCloseRef = React.useRef(false);

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === "function" ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }

        // This sets the cookie to keep the sidebar state.
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      },
      [setOpenProp, open]
    );

    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(() => {
      return isMobile ? setOpenMobile(open => !open) : setOpen(open => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);

    // Clear hover state when sidebar is manually closed
    React.useEffect(() => {
      if (!open) {
        isManualCloseRef.current = true;
        setIsHovered(false);
        // Reset the flag after a brief delay to allow hover to work again
        const timeout = setTimeout(() => {
          isManualCloseRef.current = false;
        }, 500);
        return () => clearTimeout(timeout);
      }
    }, [open]);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const effectiveOpen = open || (expandOnHover && isHovered);
    const state = effectiveOpen ? "expanded" : "collapsed";

    const contextValue = React.useMemo<SidebarContextType>(
      () => ({
        state,
        open: effectiveOpen,
        baseOpen: open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        expandOnHover,
        isHovered,
        setIsHovered,
        overlayOnHover,
        isManualCloseRef,
      }),
      [
        state,
        effectiveOpen,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        expandOnHover,
        isHovered,
        overlayOnHover,
      ]
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            style={
              {
                "--sidebar-width": SIDEBAR_WIDTH,
                "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
                ...style,
              } as React.CSSProperties
            }
            className={cn(
              "al-group/sidebar-wrapper al-flex al-min-h-svh al-w-full has-[[data-variant=inset]]:al-bg-sidebar",
              className
            )}
            ref={ref}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = "SidebarProvider";

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
  }
>(
  (
    {
      side = "left",
      variant = "sidebar",
      collapsible = "offcanvas",
      className,
      children,
      ...props
    },
    ref
  ) => {
    const {
      isMobile,
      state,
      baseOpen,
      openMobile,
      setOpenMobile,
      expandOnHover,
      setIsHovered,
      overlayOnHover,
      isHovered,
      isManualCloseRef,
    } = useSidebar();
    const hoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleMouseEnter = React.useCallback(() => {
      if (!expandOnHover || baseOpen || isManualCloseRef.current) return; // Don't set hover if already manually opened or just manually closed
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setIsHovered(true);
    }, [expandOnHover, setIsHovered, baseOpen, isManualCloseRef]);

    const handleMouseLeave = React.useCallback(() => {
      if (!expandOnHover || baseOpen) return; // Don't clear hover if manually opened
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, 300);
    }, [expandOnHover, setIsHovered, baseOpen]);

    React.useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    if (collapsible === "none") {
      return (
        <div
          className={cn(
            "al-flex al-h-full al-w-[--sidebar-width] al-flex-col al-bg-sidebar al-text-sidebar-foreground",
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            className="al-w-[--sidebar-width] al-bg-sidebar al-p-0 al-text-sidebar-foreground [&>button]:al-hidden"
            style={
              {
                "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
            side={side}
          >
            <div className="al-flex al-h-full al-w-full al-flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <div
        ref={ref}
        className="al-group al-peer al-hidden md:al-block al-text-sidebar-foreground"
        data-state={state}
        data-collapsible={state === "collapsed" ? collapsible : ""}
        data-variant={variant}
        data-side={side}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            "al-duration-200 al-relative al-h-svh al-w-[--sidebar-width] al-bg-transparent al-transition-[width] al-ease-linear",
            "group-data-[collapsible=offcanvas]:al-w-0",
            "group-data-[side=right]:al-rotate-180",
            variant === "floating" || variant === "inset"
              ? "group-data-[collapsible=icon]:al-w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]"
              : "group-data-[collapsible=icon]:al-w-[--sidebar-width-icon]"
          )}
          style={
            overlayOnHover && isHovered && !baseOpen && state === "expanded"
              ? ({
                  width:
                    variant === "floating" || variant === "inset"
                      ? "calc(var(--sidebar-width-icon) + 1rem)"
                      : "var(--sidebar-width-icon)",
                } as React.CSSProperties)
              : undefined
          }
        />
        <div
          className={cn(
            "al-duration-200 al-fixed al-inset-y-0 al-z-10 al-hidden al-h-svh al-w-[--sidebar-width] al-transition-[left,right,width] al-ease-linear md:al-flex",
            side === "left"
              ? "al-left-0 group-data-[collapsible=offcanvas]:al-left-[calc(var(--sidebar-width)*-1)]"
              : "al-right-0 group-data-[collapsible=offcanvas]:al-right-[calc(var(--sidebar-width)*-1)]",
            // Adjust the padding for floating and inset variants.
            variant === "floating" || variant === "inset"
              ? "al-p-2 group-data-[collapsible=icon]:al-w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]"
              : "group-data-[collapsible=icon]:al-w-[--sidebar-width-icon] group-data-[side=left]:al-border-r group-data-[side=right]:al-border-l",
            className
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="al-flex al-h-full al-w-full al-flex-col al-bg-sidebar group-data-[variant=floating]:al-rounded-lg group-data-[variant=floating]:al-border group-data-[variant=floating]:al-border-sidebar-border group-data-[variant=floating]:al-shadow"
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
);
Sidebar.displayName = "Sidebar";

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ className, onClick, title = "Toggle Sidebar", ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      title={title}
      className={cn("al-h-7 al-w-7", className)}
      onClick={event => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <IconLayoutSidebar />
      <span className="al-sr-only">Toggle Sidebar</span>
    </Button>
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = React.forwardRef<HTMLButtonElement, React.ComponentProps<"button">>(
  ({ className, ...props }, ref) => {
    const { toggleSidebar } = useSidebar();

    return (
      <button
        ref={ref}
        data-sidebar="rail"
        aria-label="Toggle Sidebar"
        tabIndex={-1}
        onClick={toggleSidebar}
        title="Toggle Sidebar"
        className={cn(
          "al-absolute al-inset-y-0 al-z-20 al-hidden al-w-4 al--translate-x-1/2 al-transition-all al-ease-linear after:al-absolute after:al-inset-y-0 after:al-left-1/2 after:al-w-[2px] hover:after:al-bg-sidebar-border group-data-[side=left]:al--right-4 group-data-[side=right]:al-left-0 sm:al-flex",
          "[[data-side=left]_&]:al-cursor-w-resize [[data-side=right]_&]:al-cursor-e-resize",
          "[[data-side=left][data-state=collapsed]_&]:al-cursor-e-resize [[data-side=right][data-state=collapsed]_&]:al-cursor-w-resize",
          "group-data-[collapsible=offcanvas]:al-translate-x-0 group-data-[collapsible=offcanvas]:after:al-left-full group-data-[collapsible=offcanvas]:hover:al-bg-sidebar",
          "[[data-side=left][data-collapsible=offcanvas]_&]:al--right-2",
          "[[data-side=right][data-collapsible=offcanvas]_&]:al--left-2",
          className
        )}
        {...props}
      />
    );
  }
);
SidebarRail.displayName = "SidebarRail";

const SidebarInset = React.forwardRef<HTMLDivElement, React.ComponentProps<"main">>(
  ({ className, ...props }, ref) => {
    return (
      <main
        ref={ref}
        className={cn(
          "al-relative al-flex al-min-h-svh al-flex-1 al-flex-col al-bg-background",
          "peer-data-[variant=inset]:al-min-h-[calc(100svh-theme(spacing.4))] md:peer-data-[variant=inset]:al-m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:al-ml-2 md:peer-data-[variant=inset]:al-ml-0 md:peer-data-[variant=inset]:al-rounded-xl md:peer-data-[variant=inset]:al-shadow",
          className
        )}
        {...props}
      />
    );
  }
);
SidebarInset.displayName = "SidebarInset";

const SidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        "al-h-8 al-w-full al-bg-background al-shadow-none focus-visible:al-ring-2 focus-visible:al-ring-sidebar-ring",
        className
      )}
      {...props}
    />
  );
});
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-sidebar="header"
        className={cn("al-flex al-flex-col al-gap-2 al-p-2", className)}
        {...props}
      />
    );
  }
);
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-sidebar="footer"
        className={cn("al-flex al-flex-col al-gap-2 al-p-2", className)}
        {...props}
      />
    );
  }
);
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn("al-mx-2 al-w-auto al-bg-sidebar-border", className)}
      {...props}
    />
  );
});
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-sidebar="content"
        className={cn(
          "al-flex al-min-h-0 al-flex-1 al-flex-col al-gap-2 al-overflow-auto group-data-[collapsible=icon]:al-overflow-hidden",
          className
        )}
        {...props}
      />
    );
  }
);
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-sidebar="group"
        className={cn("al-relative al-flex al-w-full al-min-w-0 al-flex-col al-p-2", className)}
        {...props}
      />
    );
  }
);
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        "al-duration-200 al-flex al-h-8 al-shrink-0 al-items-center al-rounded-md al-px-2 al-text-xs al-font-medium al-text-sidebar-foreground/70 al-outline-none al-ring-sidebar-ring al-transition-[margin,opa] al-ease-linear focus-visible:al-ring-2 [&>svg]:al-size-4 [&>svg]:al-shrink-0",
        "group-data-[collapsible=icon]:al--mt-8 group-data-[collapsible=icon]:al-opacity-0",
        className
      )}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        "al-absolute al-right-3 al-top-3.5 al-flex al-aspect-square al-w-5 al-items-center al-justify-center al-rounded-md al-p-0 al-text-sidebar-foreground al-outline-none al-ring-sidebar-ring al-transition-transform hover:al-bg-sidebar-accent hover:al-text-sidebar-accent-foreground focus-visible:al-ring-2 [&>svg]:al-size-4 [&>svg]:al-shrink-0",
        // Increases the hit area of the button on mobile.
        "after:al-absolute after:al--inset-2 after:md:al-hidden",
        "group-data-[collapsible=icon]:al-hidden",
        className
      )}
      {...props}
    />
  );
});
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-sidebar="group-content"
      className={cn("al-w-full al-text-sm", className)}
      {...props}
    />
  )
);
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      data-sidebar="menu"
      className={cn("al-flex al-w-full al-min-w-0 al-flex-col al-gap-1", className)}
      {...props}
    />
  )
);
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      data-sidebar="menu-item"
      className={cn("al-group/menu-item al-relative", className)}
      {...props}
    />
  )
);
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
  "al-peer/menu-button al-flex al-w-full al-items-center al-gap-2 al-overflow-hidden al-rounded-md al-p-2 al-text-left al-text-sm al-outline-none al-ring-sidebar-ring al-transition-[width,height,padding] hover:al-bg-sidebar-accent hover:al-text-sidebar-accent-foreground focus-visible:al-ring-2 active:al-bg-sidebar-accent active:al-text-sidebar-accent-foreground disabled:al-pointer-events-none disabled:al-opacity-50 al-group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:al-pointer-events-none aria-disabled:al-opacity-50 data-[active=true]:al-bg-sidebar-accent data-[active=true]:al-font-medium data-[active=true]:al-text-sidebar-accent-foreground data-[state=open]:hover:al-bg-sidebar-accent data-[state=open]:hover:al-text-sidebar-accent-foreground group-data-[collapsible=icon]:al-!size-8 group-data-[collapsible=icon]:al-!p-2 [&>span:last-child]:al-truncate [&>svg]:al-size-4 [&>svg]:al-shrink-0",
  {
    variants: {
      variant: {
        default: "hover:al-bg-sidebar-accent hover:al-text-sidebar-accent-foreground",
        outline:
          "al-bg-background al-shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:al-bg-sidebar-accent hover:al-text-sidebar-accent-foreground hover:al-shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "al-h-8 al-text-sm",
        sm: "al-h-7 al-text-xs",
        lg: "al-h-12 al-text-sm group-data-[collapsible=icon]:al-!p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = "default",
      size = "default",
      tooltip,
      className,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const { isMobile, state } = useSidebar();

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === "string") {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <TooltipCore>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={state !== "collapsed" || isMobile}
          {...tooltip}
        />
      </TooltipCore>
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        "al-absolute al-right-1 al-top-1.5 al-flex al-aspect-square al-w-5 al-items-center al-justify-center al-rounded-md al-p-0 al-text-sidebar-foreground al-outline-none al-ring-sidebar-ring al-transition-transform hover:al-bg-sidebar-accent hover:al-text-sidebar-accent-foreground focus-visible:al-ring-2 al-peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:al-size-4 [&>svg]:al-shrink-0",
        // Increases the hit area of the button on mobile.
        "after:al-absolute after:al--inset-2 after:md:al-hidden",
        "al-peer-data-[size=sm]/menu-button:top-1",
        "al-peer-data-[size=default]/menu-button:top-1.5",
        "al-peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:al-hidden",
        showOnHover &&
          "al-group-focus-within/menu-item:opacity-100 al-group-hover/menu-item:opacity-100 data-[state=open]:al-opacity-100 al-peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:al-opacity-0",
        className
      )}
      {...props}
    />
  );
});
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-sidebar="menu-badge"
      className={cn(
        "al-absolute al-right-1 al-flex al-h-5 al-min-w-5 al-items-center al-justify-center al-rounded-md al-px-1 al-text-xs al-font-medium al-tabular-nums al-text-sidebar-foreground al-select-none al-pointer-events-none",
        "al-peer-hover/menu-button:text-sidebar-accent-foreground al-peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "al-peer-data-[size=sm]/menu-button:top-1",
        "al-peer-data-[size=default]/menu-button:top-1.5",
        "al-peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:al-hidden",
        className
      )}
      {...props}
    />
  )
);
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    showIcon?: boolean;
  }
>(({ className, showIcon = false, ...props }, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn("al-rounded-md al-h-8 al-flex al-gap-2 al-px-2 al-items-center", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton className="al-size-4 al-rounded-md" data-sidebar="menu-skeleton-icon" />
      )}
      <Skeleton
        className="al-h-4 al-flex-1 al-max-w-[--skeleton-width]"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      data-sidebar="menu-sub"
      className={cn(
        "al-mx-3.5 al-flex al-min-w-0 al-translate-x-px al-flex-col al-gap-1 al-border-l al-border-sidebar-border al-px-2.5 al-py-0.5",
        "group-data-[collapsible=icon]:al-hidden",
        className
      )}
      {...props}
    />
  )
);
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(
  ({ ...props }, ref) => <li ref={ref} {...props} />
);
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<"a"> & {
    asChild?: boolean;
    size?: "sm" | "md";
    isActive?: boolean;
  }
>(({ asChild = false, size = "md", isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "al-flex al-h-7 al-min-w-0 al--translate-x-px al-items-center al-gap-2 al-overflow-hidden al-rounded-md al-px-2 al-text-sidebar-foreground al-outline-none al-ring-sidebar-ring hover:al-bg-sidebar-accent hover:al-text-sidebar-accent-foreground focus-visible:al-ring-2 active:al-bg-sidebar-accent active:al-text-sidebar-accent-foreground disabled:al-pointer-events-none disabled:al-opacity-50 aria-disabled:al-pointer-events-none aria-disabled:al-opacity-50 [&>span:last-child]:al-truncate [&>svg]:al-size-4 [&>svg]:al-shrink-0 [&>svg]:al-text-sidebar-accent-foreground",
        "data-[active=true]:al-bg-sidebar-accent data-[active=true]:al-text-sidebar-accent-foreground",
        size === "sm" && "al-text-xs",
        size === "md" && "al-text-sm",
        "group-data-[collapsible=icon]:al-hidden",
        className
      )}
      {...props}
    />
  );
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
